import { Plan } from "../Entities/Plan";
import { TransformedPlan } from "../Interface";

const getPlanFeatures = (plan: Plan): string[] => {
  const features = [];

  if (plan.limits.jobPosts === -1) {
    features.push("Unlimited active Job Posts");
  } else {
    features.push(`${plan.limits.jobPosts} active Job Posts`);
  }

  if (plan.limits.applicationsProcessed === -1) {
    features.push("Unlimited Assessments");
  } else {
    features.push(`${plan.limits.applicationsProcessed} Assessments`);
  }

  if (plan.limits.teamMembers === -1) {
    features.push("Unlimited recruiters");
  } else {
    features.push(`Up to ${plan.limits.teamMembers} recruiters`);
  }

  return features;
};

const getPlanDescription = (planName: string): string => {
  const planNameLowerCase = planName.toLowerCase().trim();

  if (planNameLowerCase.includes("basic")) {
    return "For growing teams";
  } else if (planNameLowerCase.includes("professional")) {
    return "For high-volume hiring";
  } else if (planNameLowerCase.includes("enterprise")) {
    return "For custom needs";
  }

  return "Perfect for your business needs";
};

const GST_RATE = 0.18;

const extractBasePrice = (
  inclusivePrice: number,
  currencyCode: "INR" | "USD"
): number => {
  if (currencyCode !== "INR") return inclusivePrice;
  return Math.round(inclusivePrice / (1 + GST_RATE));
};

const getPlanPrice = (
  plan: Plan,
  userCurrencyCode: "INR" | "USD"
): { monthly: number; annually: number } => {
  const currencyVariant = plan.variants.filter(
    (v) => v.currency.toLowerCase() === userCurrencyCode.toLowerCase()
  );

  const monthlyPrice =
    currencyVariant.find((v) => v.billingPeriod.toLowerCase() === "month")
      ?.price || 0;
  const yearlyPrice =
    currencyVariant.find((v) => v.billingPeriod.toLowerCase() === "year")
      ?.price || 0;

  return {
    monthly: extractBasePrice(monthlyPrice, userCurrencyCode),
    annually: extractBasePrice(yearlyPrice, userCurrencyCode),
  };
};

const formatPlanName = (planName: string): string => {
  return planName.replace(/\s*\(.*$/, "").trim();
};

const transformPlanData = (
  apiPlans: Plan[],
  orgId: string,
  userCurrencyCode: "INR" | "USD",
  userCurrencySymbol: "₹" | "$"
): TransformedPlan[] => {
  if (!apiPlans) return [];

  const orgEnterPrisePlan: Plan | null =
    apiPlans.find((p) => p.orgId && p.orgId === orgId) || null;

  const defaultEnterprisePlan = apiPlans.find(
    (p) => p.name.toLowerCase().includes("enterprise") && p.orgId === null
  );

  const isDefaultEnterprise = (plan: Plan) =>
    plan.orgId === null && plan.name.toLowerCase().includes("enterprise");

  const formattedPlans: TransformedPlan[] = apiPlans
    .filter(
      (plan) =>
        plan.type !== "free" &&
        plan.type !== "addon" &&
        plan.orgId === null &&
        !isDefaultEnterprise(plan)
    )
    .map((plan) => {
      const { monthly, annually } = getPlanPrice(plan, userCurrencyCode);

      return {
        _id: plan._id,
        name: plan.name.replace(/\s*\(.*$/, "").trim(),
        description: getPlanDescription(plan.name),
        price: {
          monthly: monthly,
          annually: annually,
        },
        features: getPlanFeatures(plan),
        customBranding: plan.limits.customBranding,
        customDomainLinking: plan.limits.customDomainLinking,
        isDefaultEnterprisePlan:
          plan.orgId === null && plan.name.toLowerCase().includes("enterprise"),
        isOrgEnterprisePlan: false,
        currency: userCurrencyCode,
        currencySymbol: userCurrencySymbol,
      };
    });

  // When org has enterprise plan: show only org enterprise (do not show default).
  // When org has none: show default enterprise.
  if (orgEnterPrisePlan) {
    const orgEntCurrency = orgEnterPrisePlan.variants[0].currency as "INR" | "USD";
    formattedPlans.push({
      _id: orgEnterPrisePlan._id,
      name: formatPlanName(orgEnterPrisePlan.name),
      description: getPlanDescription(orgEnterPrisePlan.name),
      price: {
        monthly: extractBasePrice(orgEnterPrisePlan.variants[0].price, orgEntCurrency),
        annually: extractBasePrice(orgEnterPrisePlan.variants[0].price, orgEntCurrency),
      },
      features: getPlanFeatures(orgEnterPrisePlan),
      customBranding: orgEnterPrisePlan.limits.customBranding,
      customDomainLinking: orgEnterPrisePlan.limits.customDomainLinking,
      isDefaultEnterprisePlan: false,
      isOrgEnterprisePlan: true,
      currency: orgEnterPrisePlan.variants[0].currency,
      currencySymbol:
        orgEnterPrisePlan.variants[0].currency === "INR" ? "₹" : "$",
    });
  } else if (defaultEnterprisePlan) {
    const defEntCurrency = defaultEnterprisePlan.variants[0].currency as "INR" | "USD";
    formattedPlans.push({
      _id: defaultEnterprisePlan._id,
      name: formatPlanName(defaultEnterprisePlan.name),
      description: getPlanDescription(defaultEnterprisePlan.name),
      price: {
        monthly: extractBasePrice(defaultEnterprisePlan.variants[0].price, defEntCurrency),
        annually: extractBasePrice(defaultEnterprisePlan.variants[0].price, defEntCurrency),
      },
      features: getPlanFeatures(defaultEnterprisePlan),
      customBranding: defaultEnterprisePlan.limits.customBranding,
      customDomainLinking: defaultEnterprisePlan.limits.customDomainLinking,
      isDefaultEnterprisePlan: true,
      isOrgEnterprisePlan: false,
      currency: defaultEnterprisePlan.variants[0].currency,
      currencySymbol:
        defaultEnterprisePlan.variants[0].currency === "INR" ? "₹" : "$",
    });
  }

  return formattedPlans;
};

const getPlanAnnualModePricing = (
  plan: TransformedPlan
): {
  actualMonthlyPrice: number;
  annualModeMonthlyPrice: number;
  totalAnnualSavings: number;
} => {
  const annualModeMonthlyPrice = plan.price.annually / 12;
  const monthlyPrice = plan.price.monthly;

  const totalAnnualSavings = monthlyPrice * 12 - plan.price.annually;

  return {
    actualMonthlyPrice: monthlyPrice,
    annualModeMonthlyPrice: annualModeMonthlyPrice,
    totalAnnualSavings: totalAnnualSavings,
  };
};

export {
  extractBasePrice,
  getPlanFeatures,
  getPlanDescription,
  getPlanPrice,
  formatPlanName,
  transformPlanData,
  getPlanAnnualModePricing,
};
